<template lang="pug">
div
  v-menu(v-model="show" rounded="lg" allow-overflow :close-on-content-click="false" offset-y top max-height="345")
    template(#activator="{ on }")
      v-sheet.text-sm.round-lg.clickable(flat outlined :class="isError ? 'error-border-menu mt-2' : 'border-menu'" @click="fetchProducts")
        v-row.py-1(no-gutters align="center" style="min-height: 38px;")
          v-col.pl-3
            .text-sm(v-if="selectedItem") {{ selectedItem.name }}
            .text-sm(v-else-if="item.productItem") {{ item.productItem.name }}
            .text-sm.neutral400--text(v-else) Select item
          v-col.shrink.pr-3(v-if="isFetching")
            v-progress-circular.ma-auto(:width="2" :size="15" color="primary" indeterminate)
          v-col.shrink.pr-2(v-else)
            v-icon(size=20 style="margin-top: 2px;") $yzi-chevron-down
      div.text-xxs.danger500--text.ml-3.mt-1.mb-2(v-show="isError") {{ errorMessage }}

    .text-xs.white
      .search-input
        v-text-field.mb-2.mx-4.text-sm.round-xl(v-model="itemName" outlined placeholder="Search inventory and services" dense hide-details autofocus)
        v-divider
      div.d-flex.justify-center(v-if="isFetching")
        v-progress-linear(:width="3" rounded color="primary" indeterminate)
      .d-flex.align-center.justify-space-between.px-4.py-3
        .font-semibold.mr-6 Existing treatments
        y-pagination(v-model="page" :pageSize="pageSize" :count="count" :loading="isFetching") 
      div(v-for="item in productItems" @click="onSelectItem(item)")
        .d-flex.flex-column.py-3.px-4(:class="[{ 'hover-bg':!item.disabled, 'disabled-item':item.disabled, 'clickable': !item.disabled }]")
          .text-sm.font-normal(v-if="item.uom") {{ item.name }}  ({{item.uom}})
          .text-sm.font-normal(v-else) {{ item.name }}
          .d-flex
            .d-flex.align-center.mr-4
              template(v-if="hasStock(item)")
                v-icon.mr-1(size="11" color="success") $yzi-circle
                .text-xs.font-normal.neutral500--text {{ $t('in_stock') }}
              template(v-else)
                v-icon.mr-1(size="11" color="danger") $yzi-circle
                .text-xs.font-normal.neutral500--text {{ $t('out_of_stock') }}
            .d-flex.align-center.mr-4
              v-icon.mr-1(size="13") {{ getTypeIcon(item) }}
              .text-xs.font-normal.neutral500--text {{ item.type | capitalize }}
            template(v-if="item.groups && item.groups.length > 0")
              .d-flex.align-center.mr-4
                v-icon.mr-1(size="13") $yzi-folder
                .text-xs.font-normal.neutral500--text {{ item.groups.join(', ') | capitalize }}
          .text-xxs.font-normal.italic.danger500--text(v-if="item.hasError") {{ item.errorMessage }}
        v-divider
      template(v-if="!productItems.length")
        .px-4.py-3 {{ $t('no_result_match') }}
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    item: {
      type: Object,
      required: true
    },
    isOTC: {
      type: Boolean
    },
    isError: {
      type: Boolean
    },
    errorMessage: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    isLoading: false,
    isFetching: false,
    show: false,
    itemName: null,
    diagnoses: [],
    toDeleteItem: null,
    isDeleting: false,
    openDeleteConfirmationDialog: false,
    selectedItem: null,
    page: 1,
    pageSize: 10,
    count: 0,
    productItems: [],
    initialized: false
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    })
  },
  watch: {
    show(val) {
      this.$emit('dropdownOpenClosed', val)
    },
    page () {
      this.fetchProducts()
    },
    itemName () {
      this.searchStart()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    searchStart: debounce(function () {
      this.page = 1
      this.fetchProducts()
    }, 750),
    onSelectItem (item) {
      if (item.disabled) {
        return
      }
      if (item.medication) {
        if (!item.medication.instruction_enabled) {
          item.medication.dosage_unit = null
          item.medication.dosage_value = null
          item.medication.frequency = null
          item.medication.duration = null
          item.medication.instruction = null
          item.medication.precaution = null
          item.medication.indication = null
        }
      }
      this.selectedItem = item
      this.itemName = ''
      this.$set(this.item, 'productItem', item)
      this.$emit('changed')
      this.$emit('dropdownOpenClosed', false)
    },
    async fetchProducts () {
      if (this.show && !this.initialized) {
        this.show = false
      }
      try {
        this.isFetching = true
        let params = {
          page: this.page,
          page_size: this.pageSize,
          search: this.itemName || null,
          status: 'published',
          ordering: '-inventory_qty,medication,name'
        }
        const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
        const { data } = await this.$axios.get(`/clinics/${companySlug}/treatment_products`, { params })
        if (data.count > 0) {
          const newData = data.results.map(result => {
            return {
              ...result,
              disabled: this.isDisabled(result),
              hasError: this.hasError(result),
              errorMessage: this.getErrorMessage(result),
            }
          })
          this.productItems = newData
        } else {
          this.productItems = []
        }
        this.count = data.count
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
        this.show = true
        this.initialized = true
      }
    },
    hasStock(item) {
      if (item.type === 'bundle') {
        if (item.bundle.available_qty === null) {
          return true
        } else {
          return item.bundle.available_qty > 0
        }
      }
      if (!item.inventory_enabled) {
        return true
      }
      if (item.inventory_qty > 0) {
        return true
      }
      return false
    },
    getTypeIcon (item) {
      if (item.medication) {
        return '$yzi-colors'
      }
      if (item.type === 'documents') {
        return '$yzi-file-06'
      }
      return 'mdi-briefcase-outline'
    },
    hasError (item) {
      return item.type === 'documents' && this.isOTC
    },
    getErrorMessage (item) {
      if (this.hasError(item)) {
        return 'Medical document cannot be added for OTC'
      }
      return null
    },
    isDisabled (item) {
      return !this.hasStock(item) || this.hasError(item)
    }
  }
}
</script>

<style scoped>
.border-menu {
  border: 1px solid var(--v-basic500);
  box-shadow: none;
}
.error-border-menu {
  border: 1px solid var(--v-danger500);
  box-shadow: none;
}
.search-input {
  position: sticky;
  top: 0px;
  background-color: #FFFFFF !important;
  border-color: #FFFFFF !important;
  z-index: 4;
  padding-top: 8px;
}
.disabled-item {
  background-color: var(--v-neutral300);
  color: var(--v-neutral500);
}
</style>